import type { WorkspaceNavItemResolved } from "@/components/app-nav";

/** Presentación desktop del sidebar. No altera workspaceNavItems / navItemsForRole. */
export const SIDEBAR_NAV_GROUPS = [
  { id: "principal", label: "Principal", paths: ["/app/dashboard", "/app/metricas"] },
  {
    id: "comercio",
    label: "Comercio",
    paths: ["/app/pedidos", "/app/pesaje", "/app/ordenes/devoluciones", "/app/clientes", "/app/descuentos", "/app/produccion", "/app/eventos"],
  },
  {
    id: "catalogo",
    label: "Catálogo",
    paths: ["/app/catalogacion", "/app/operaciones", "/app/media", "/app/digital"],
  },
  {
    id: "operacion",
    label: "Operación",
    paths: ["/app/pagos", "/app/facturacion", "/app/logistica", "/app/pos", "/app/salon", "/app/reservas", "/app/kds"],
  },
  // El nicho servicios aporta 12 pantallas. Agrupadas en un solo bloque quedaban
  // ilegibles, así que se parten por momento de uso: lo que se opera todos los días,
  // lo que se configura una vez, y lo que mira el dueño.
  {
    id: "agenda",
    label: "Agenda",
    paths: ["/app/agenda", "/app/turnos", "/app/horarios", "/app/lista-espera"],
  },
  {
    id: "servicios-catalogo",
    label: "Catálogo de servicios",
    paths: ["/app/servicios", "/app/clases", "/app/profesionales", "/app/packs", "/app/formularios"],
  },
  {
    id: "servicios-negocio",
    label: "Negocio",
    paths: [
      "/app/metricas-servicios",
      "/app/liquidaciones",
      "/app/actividad-cobros",
      "/app/resenas",
      "/app/recursos",
    ],
  },
  { id: "experiencia", label: "Experiencia", paths: ["/app/experience", "/app/renata"] },
  // SCRUM-252: bloque Sistema/Configuración explícito (antes dependía del fallback
  // "todo lo no mapeado cae acá") — Hardware, Pantallas, Cumplimiento/Privacidad y
  // Configuración son las capacidades operativas/administrativas del tenant.
  // Analytics GA4 se movió al hub de Métricas (ver principal) y ya no es un acceso propio.
  {
    id: "sistema",
    label: "Sistema",
    paths: ["/app/hardware", "/app/pantallas", "/app/cumplimiento/solicitudes", "/app/cumplimiento/privacidad", "/app/settings"],
  },
] as const;

export type SidebarNavGroupView<T extends { to: string }> = {
  id: string;
  label: string;
  items: T[];
};

export function groupSidebarNavItems<T extends { to: string }>(items: T[]): SidebarNavGroupView<T>[] {
  const byPath = new Map(items.map((item) => [item.to, item]));
  const used = new Set<string>();
  const groups: SidebarNavGroupView<T>[] = [];

  for (const group of SIDEBAR_NAV_GROUPS) {
    const grouped: T[] = [];
    for (const path of group.paths) {
      const item = byPath.get(path);
      if (!item) continue;
      grouped.push(item);
      used.add(item.to);
    }
    if (grouped.length > 0) {
      groups.push({ id: group.id, label: group.label, items: grouped });
    }
  }

  const rest = items.filter((item) => !used.has(item.to));
  if (rest.length === 0) return groups;

  const sistema = groups.find((group) => group.id === "sistema");
  if (sistema) {
    sistema.items.push(...rest);
    return groups;
  }
  groups.push({ id: "mas", label: "Más", items: rest });
  return groups;
}

export function isSidebarNavItemActive(pathname: string, to: string) {
  if (to === "/app/dashboard") return pathname === "/app/dashboard" || pathname === "/app";
  return pathname === to || pathname.startsWith(`${to}/`);
}

export type SidebarNavItem = WorkspaceNavItemResolved;
